import { PageContainer } from "../../components/v2/PageContainer";
import { Box, Button, Card, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { NotificationList } from "../../components/v2/notification/NotificationList";
import { subtractDays, GetNotifications, generateGtmEvent } from "../../Helpers";
import { UserNotification } from "@backend-types";
import { NotificationListLoader } from "../../components/v2/notification/NotificationListLoader";
import { UserContext } from "../../context/UserContext";
import { NotificationCategory, UserCtx } from "../../interfaces";

const NOTIFICATION_CATEGORY: NotificationCategory[] = [
  {
    id: "1",
    name: "Today",
    whiteSpace: "break-spaces",
    filter: (notification: UserNotification) =>
      new Date(notification.created_at).getTime() > subtractDays(Date.now(), 1).getTime(),
  },
  {
    id: "2",
    name: "Earlier",
    whiteSpace: "break-spaces",
    filter: (notification: UserNotification) =>
      new Date(notification.created_at).getTime() <=
      subtractDays(Date.now(), 1).getTime(),
  },
];
const getHeight = () =>
  document.body.scrollHeight - document.documentElement.clientHeight;

const getScrollY = () => window.scrollY;

interface NotificationPageProps {
  isNotificationEnabled: boolean;
}
export const NotificationPage = (props: NotificationPageProps) => {
  const { isNotificationEnabled = true } = props;
  const [notifications, setNotifications] = React.useState<UserNotification[]>(
    [] as UserNotification[]
  );
  const userCtx = useContext(UserContext) as UserCtx;
  const [windowHeight, setWindowHeight] = useState(getHeight());
  const [scrollY, setScrollY] = useState(getScrollY());
  const [reachedBottom, setReachedBottom] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentPage >= totalPages) return;
    if (currentPage === 0) {
      setLoading(true);
    }
    const getData = setTimeout(() => {
      GetNotifications(currentPage).then((response) => {
        setLoading(false);
        setNotifications((prev) => [...prev, ...response.results]);
        setTotalPages(response.totalResults / response.resultsPerPage);
        setReachedBottom(false);
        setWindowHeight(getHeight());
      });
    }, 1000);
    return () => clearTimeout(getData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const resizeListener = () => {
      setWindowHeight(getHeight());
    };
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  useEffect(() => {
    const scrollListener = () => {
      setScrollY(getScrollY());
    };
    window.addEventListener("scroll", scrollListener);
    return () => {
      window.removeEventListener("scroll", scrollListener);
    };
  }, []);

  useEffect(() => {
    if (windowHeight - scrollY < 5) {
      let data = setTimeout(() => {
        setReachedBottom(true);
      }, 500);
      return () => clearTimeout(data);
    }
  }, [windowHeight, scrollY]);

  useEffect(() => {
    if (notifications.length > 0 && reachedBottom && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedBottom]);

  const handleToggleNotifications = async () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "settings",
      text: `turn ${isNotificationEnabled ? "off" : "on"} notifications`,
      component: "notifications",
    });
    try {
      await userCtx.updateDbUser(userCtx.dbUser.id, {
        notifications_enabled: !userCtx.dbUser.notifications_enabled,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PageContainer>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h1">Notifications</Typography>
        <Box sx={{ width: "100%", marginBottom: "20px" }}>
          {!isNotificationEnabled && (
            <Card
              sx={{
                background: "#0092E017 0% 0% no-repeat padding-box",
                border: ".5px solid #DEDEDE",
                borderRadius: "15px",
                marginTop: "32px",
                padding: "32px",
              }}>
              <Stack direction={"row"} alignItems={"center"} gap={"10px"}>
                <i className={"far fa-info-circle"}></i>
                <Typography variant={"body2"}>
                  {" "}
                  Your notifications are turned off{" "}
                </Typography>
              </Stack>
              <Button
                size={"small"}
                variant="outlined"
                onClick={() => {
                  handleToggleNotifications();
                }}
                sx={{
                  borderRadius: "8px",
                  marginTop: "16px",
                  fontSize: "16px",
                  "&:hover": {
                    transform: "none",
                  },
                }}>
                Turn on notifications
              </Button>
            </Card>
          )}
          <Card
            sx={{
              border: "2px solid #DEDEDE",
              borderRadius: "15px",
              marginTop: "32px",
              padding: "32px 28px",
            }}>
            {loading && <NotificationListLoader />}
            <NotificationList
              noNotificationMessage={loading ? "" : "No recent alerts"}
              notificationCategory={NOTIFICATION_CATEGORY}
              notifications={notifications}
              categoryFontSize={"24px"}
            />
            {notifications.length !== 0 && reachedBottom && currentPage < totalPages && (
              <CircularProgress
                sx={{
                  position: "fixed",
                  bottom: "0px",
                  left: "60%",
                }}
              />
            )}
          </Card>
        </Box>
      </Box>
    </PageContainer>
  );
};
